import { component } from 'picoapp';

export default component((node) => {
  /**
   * Data + elements
   */
  const shippingDateOutput = node.querySelector('.js-shippingDate');

  const config = JSON.parse(node.dataset.config);
  const { dailyShippingTime, shippingDays, allowedWeekdays, googleCalendarAPIKey, googleCalendarID, reverseDates } = config;

  // SAFETY FIX: If metafield is empty, default to Mon-Fri [1,2,3,4,5]
  // We also force them to be numbers so .includes() doesn't fail.
  const safeWeekdays = (allowedWeekdays || [1, 2, 3, 4, 5]).map(Number);
  const safeShippingDays = Number(shippingDays) || 0;

  const weekdayStrings = window.theme.strings.weekdays;

  const parseCutoffTime = (value) => {
    if (value === null || value === undefined || value === '') return { hours: 0, minutes: 0 };
    if (typeof value === 'number' && Number.isFinite(value)) return { hours: value, minutes: 0 };

    const str = String(value).trim();

    // Accept: "16", "16.30", "16:30", "16,30"
    const normalized = str.replace(',', ':').replace('.', ':');
    const parts = normalized
      .split(':')
      .map((p) => p.trim())
      .filter(Boolean);

    const hours = Number(parts[0]);
    const minutes = parts.length > 1 ? Number(parts[1]) : 0;

    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return { hours: 0, minutes: 0 };
    return { hours, minutes };
  };

  /**
   * Functions
   */
  const getGoogleData = () =>
    new Promise((resolve) => {
      // SAFETY FIX: Prevent crash if API keys aren't added yet
      if (!googleCalendarAPIKey || !googleCalendarID) {
        return resolve([]);
      }

      window.gapi.load('client', () => {
        window.gapi.client
          .init({
            apiKey: googleCalendarAPIKey,
            discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest'],
          })
          .then(() =>
            window.gapi.client.calendar.events.list({
              calendarId: googleCalendarID,
              singleEvents: true,
              orderBy: 'startTime',
            }),
          )
          .then((response) => {
            resolve(response.result.items);
          })
          .catch((error) => {
            console.error('Google Calendar Error:', error); // eslint-disable-line
            resolve([]);
          });
      });
    });

  const setShippingDate = async () => {
    const googleData = await getGoogleData();

    const shippingDate = new Date();
    let daysAdded = 0;
    const cutoff = parseCutoffTime(dailyShippingTime);

    const getDatesBetween = (startDate, endDate) => {
      const dates = [];
      let currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      return dates;
    };

    const testDateAgainstCalendar = (dateToTest) => {
      const blockingItem = googleData.find((item) => {
        // SAFETY FIX: Use optional chaining (?.) just in case Google returns a weird event
        const startStr = item.start?.dateTime || item.start?.date;
        const endStr = item.end?.dateTime || item.end?.date;

        if (!startStr || !endStr) return false;

        const dates = getDatesBetween(new Date(startStr), new Date(endStr));
        return dates.find((d) => d.toDateString() === dateToTest.toDateString());
      });

      return !blockingItem;
    };

    // Helper to check if a specific date is a valid shipping day
    const isValidDay = (dateToCheck) => {
      return safeWeekdays.includes(dateToCheck.getDay()) && testDateAgainstCalendar(dateToCheck);
    };

    // 1. Is it currently past the cutoff time today?
    const afterCutoffToday = shippingDate.getHours() > cutoff.hours || (shippingDate.getHours() === cutoff.hours && shippingDate.getMinutes() > cutoff.minutes);

    // 2. If it's past cutoff, push to tomorrow immediately so today isn't counted
    if (afterCutoffToday) {
      shippingDate.setDate(shippingDate.getDate() + 1);
    }

    // 3. Keep pushing the date forward until we land on a valid starting day
    while (!isValidDay(shippingDate)) {
      shippingDate.setDate(shippingDate.getDate() + 1);
    }

    // 4. Now that we are on a valid processing day, add the required transit/shipping days
    while (daysAdded < safeShippingDays) {
      shippingDate.setDate(shippingDate.getDate() + 1);

      // Only count this day towards transit if it's a valid business day
      if (isValidDay(shippingDate)) {
        daysAdded += 1;
      }
    }

    const date = shippingDate.getDate();
    const month = shippingDate.getMonth() + 1;
    const dateString = `${weekdayStrings[shippingDate.getDay()]} ${reverseDates ? month : date}/${reverseDates ? date : month}`; // eslint-disable-line

    if (shippingDateOutput) {
      shippingDateOutput.textContent = dateString;
    }
  };

  const init = () => {
    if (window.gapi) {
      setShippingDate();
    } else {
      window.addEventListener('googleApi:loaded', setShippingDate);
    }
  };

  /**
   * Init
   */
  init();
});
